/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * MessageBubble - Individual message display component
 */

import { useState } from 'react'
import { Check, CheckCheck, Trash2, CornerDownRight, Flag } from 'lucide-react'
import messagingService from '../../services/messagingService'
import moderationService from '../../services/moderationService'
import toast from 'react-hot-toast'

const MessageBubble = ({ message, isOwn, currentUserId, onDelete, showSender = true }) => {
  const [deleting, setDeleting] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [showReportForm, setShowReportForm] = useState(false)
  const [reportReason, setReportReason] = useState('')

  const handleReport = async () => {
    const reason = reportReason.trim()
    if (!reason) return
    try {
      setReporting(true)
      await moderationService.reportMessage(message.id, reason)
      toast.success('Reported. A moderator will review it.')
      setShowReportForm(false)
      setReportReason('')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to report message')
    } finally {
      setReporting(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this message?')) return

    try {
      setDeleting(true)
      await messagingService.deleteMessage(message.id)
      onDelete?.(message.id)
      toast.success('Message deleted')
    } catch (error) {
      toast.error('Failed to delete message')
    } finally {
      setDeleting(false)
    }
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    }
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    })
  }

  if (message.isDeleted) {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
        <div className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 italic text-sm">
          Message deleted
        </div>
      </div>
    )
  }

  return (
    <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} mb-2`}>
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}>
        <div
          className={`max-w-[75%] rounded-lg px-4 py-2 ${
            isOwn
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
          }`}
        >
          {/* Reply indicator */}
          {message.parentMessage && (
            <div
              className={`flex items-center gap-1 mb-1 pb-1 border-b text-xs ${
                isOwn
                  ? 'border-primary-400 text-primary-200'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              <CornerDownRight className="w-3 h-3" />
              <span className="truncate">
                {message.parentMessage.content.slice(0, 50)}
                {message.parentMessage.content.length > 50 && '...'}
              </span>
            </div>
          )}

          {/* Sender name (for received messages) */}
          {showSender && !isOwn && message.sender && (
            <div className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-1">
              {message.sender.firstName} {message.sender.lastName}
              {message.senderRole === 'guardian' && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  Guardian
                </span>
              )}
            </div>
          )}

          {/* Message content */}
          <p className="whitespace-pre-wrap break-words">{message.content}</p>

          {/* Footer: time and status */}
          <div
            className={`flex items-center justify-end gap-1 mt-1 text-xs ${
              isOwn ? 'text-primary-200' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            <span>{formatTime(message.createdAt)}</span>
            {isOwn &&
              (message.isRead ? (
                <CheckCheck className="w-3.5 h-3.5" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              ))}
          </div>
        </div>

        {/* Delete button (only for own messages) */}
        {isOwn && !message.isDeleted && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="ml-1 p-1 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
            title="Delete message"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        {/* Report button (for received messages) — toggles the inline form below */}
        {!isOwn && !message.isDeleted && (
          <button
            type="button"
            onClick={() => {
              // Closing via the flag toggle should clear the draft too, matching the Cancel button.
              if (showReportForm) setReportReason('')
              setShowReportForm((v) => !v)
            }}
            disabled={reporting}
            className="ml-1 p-1 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-amber-500"
            title="Report message"
          >
            <Flag className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Inline report form (replaces window.prompt, which is blocked in some embedded contexts) */}
      {showReportForm && !isOwn && !message.isDeleted && (
        <div className="mt-1 w-full max-w-[75%] rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 p-2">
          <label
            htmlFor={`report-reason-${message.id}`}
            className="block text-xs text-gray-600 dark:text-gray-300 mb-1"
          >
            Why are you reporting this message? Your report goes to a moderator.
          </label>
          <textarea
            id={`report-reason-${message.id}`}
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            rows={2}
            autoFocus
            maxLength={2000}
            className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            placeholder="Describe the problem..."
          />
          <div className="flex justify-end gap-2 mt-1">
            <button
              type="button"
              onClick={() => {
                setShowReportForm(false)
                setReportReason('')
              }}
              className="px-2 py-1 text-xs rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReport}
              disabled={reporting || !reportReason.trim()}
              className="px-2 py-1 text-xs rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {reporting ? 'Reporting...' : 'Report'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default MessageBubble
